import { create } from 'zustand';
import type { SearchDataset, SearchStatus } from './types';

interface SearchStoreState {
  datasetsByKey: Record<string, SearchDataset | undefined>;
  statusByKey: Record<string, SearchStatus | undefined>;
  errorsByKey: Record<string, unknown>;
  lastUpdatedAtByKey: Record<string, number | undefined>;
  setDataset: (key: string, dataset: SearchDataset) => void;
  setLoading: (key: string, options?: { isRefetching?: boolean }) => void;
  setError: (key: string, error: unknown) => void;
  invalidateDataset: (key: string) => void;
  clearDataset: (key: string) => void;
}

const omitKey = <T extends Record<string, unknown>>(record: T, key: string): T => {
  const { [key]: _removed, ...remaining } = record;
  return remaining as T;
};

const useSearchStore = create<SearchStoreState>((set) => ({
  datasetsByKey: {},
  statusByKey: {},
  errorsByKey: {},
  lastUpdatedAtByKey: {},
  setDataset: (key, dataset) =>
    set((state) => ({
      datasetsByKey: { ...state.datasetsByKey, [key]: dataset },
      statusByKey: { ...state.statusByKey, [key]: 'success' },
      errorsByKey: omitKey(state.errorsByKey, key),
      lastUpdatedAtByKey: { ...state.lastUpdatedAtByKey, [key]: Date.now() },
    })),
  setLoading: (key, options) =>
    set((state) => ({
      statusByKey: {
        ...state.statusByKey,
        [key]: options?.isRefetching ? 'refetching' : 'loading',
      },
      errorsByKey: omitKey(state.errorsByKey, key),
    })),
  setError: (key, error) =>
    set((state) => ({
      statusByKey: { ...state.statusByKey, [key]: 'error' },
      errorsByKey: { ...state.errorsByKey, [key]: error },
    })),
  invalidateDataset: (key) =>
    set((state) => ({
      datasetsByKey: omitKey(state.datasetsByKey, key),
      statusByKey: { ...state.statusByKey, [key]: 'idle' },
      errorsByKey: omitKey(state.errorsByKey, key),
      lastUpdatedAtByKey: omitKey(state.lastUpdatedAtByKey, key),
    })),
  clearDataset: (key) =>
    set((state) => ({
      datasetsByKey: omitKey(state.datasetsByKey, key),
      statusByKey: omitKey(state.statusByKey, key),
      errorsByKey: omitKey(state.errorsByKey, key),
      lastUpdatedAtByKey: omitKey(state.lastUpdatedAtByKey, key),
    })),
}));

export default useSearchStore;
